import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AggregationFunction, AttributeType, QueryLayerServiceConfigurationDc } from "@evergis/api";
import { isNil } from "lodash";

import {
  ComparisonOperator,
  FilterInput,
  FilterInputType,
  FilterTypeOption,
  FilterValue,
  IconToggle,
} from "@evergis/ui";

import {
  ConditionAttributeContainer,
  ConditionBlank,
  ConditionBlankInner,
  ConditionDeleteButton,
  FilterInputContainer,
  FilterInputParamSwitchContainer,
} from "../styled";

import { ConditionAttribute } from "./ConditionAttribute";

import { useGetValueOptions } from "../hooks/useGetValueOptions";
import { useFilterConditionUpdate } from "../hooks/useFilterConditionUpdate";
import { getAttributeType } from "../../../evergis/core/layer/getAttribute";
import { formatDate, getDate, DateFormat } from "../../../utils/date";
import { formatFilterValue } from "../../../utils/formatFilterValue";
import { isObject } from "../../../utils/isObject";
import { useToggle } from "../../../hooks/useToggle";

import {
  getComparisonOperatorOptions,
  getParamOperatorOptions,
  COMPARISON_OPTIONS,
} from "../utils/getComparisonOperatorOptions";

import { api } from "../../../evergis/api";
import { FeaturesFilterConditionProps, LayerFilterOption } from "../types";

export const FeaturesFilterCondition: FC<FeaturesFilterConditionProps> = memo(
  // eslint-disable-next-line max-lines-per-function
  ({ condition, updateCondition, deleteCondition, layer }) => {
    const { t } = useTranslation("layerFilter");
    const { attribute, comparisonOperator, value } = condition;
    const prevOperator = useRef(comparisonOperator);
    const prevAttribute = useRef(attribute);
    const attributeType = useMemo(() => getAttributeType(layer, attribute), [layer, attribute]);
    const [options, setOptions] = useState(getComparisonOperatorOptions(attributeType));
    const getValueOptions = useGetValueOptions(layer.name, attribute);
    const [min, setMin] = useState<number | Date | undefined>();
    const [max, setMax] = useState<number | Date | undefined>();
    const [isParam, toggleIsParam] = useToggle(false);
    const [filterValue, setFilterValue] = useState(value as FilterValue);
    const isEqlWithParams = useMemo(() => {
      return (
        layer?.type === "QueryLayerService" &&
        isObject((layer.configuration as QueryLayerServiceConfigurationDc).eqlParameters) &&
        Object.keys((layer.configuration as QueryLayerServiceConfigurationDc).eqlParameters).length > 0
      );
    }, [layer]);
    const isValueEqlParam = isEqlWithParams && typeof condition.value === "string" && condition.value.startsWith("@");

    const { changeAttribute, changeComparisonOperator, changeValue, deleteCurrentCondition } = useFilterConditionUpdate(
      condition,
      updateCondition,
      deleteCondition,
    );

    const onShown = useCallback(async () => {
      const isDate = attributeType === AttributeType.DateTime;

      const statistic = await api.statistic.getStatistic({
        name: layer.name,
        types: [AggregationFunction.Min, AggregationFunction.Max],
        attribute,
      });

      const { statisticsResult } = statistic;

      if (statisticsResult?.length) {
        setMin(formatFilterValue(statisticsResult.find(stat => stat.type === AggregationFunction.Min)?.value, isDate));
        setMax(formatFilterValue(statisticsResult.find(stat => stat.type === AggregationFunction.Max)?.value, isDate));
      }
    }, [attributeType, layer.name, attribute]);

    const selectedOption = useMemo(() => {
      return options.find(item => item.value === comparisonOperator) || COMPARISON_OPTIONS.Equals;
    }, [comparisonOperator, options]);

    const filterType: FilterInputType = useMemo(() => attributeType.toLowerCase() as FilterInputType, [attributeType]);

    const getValue: FilterValue = useMemo(() => {
      const isDate = filterType.includes("date") && !isValueEqlParam;

      if (isNil(filterValue)) {
        return "";
      }

      return Array.isArray(filterValue)
        ? filterValue
        : typeof filterValue === "string"
        ? filterValue.includes(" - ")
          ? (filterValue.split(" - ").map(val => (isDate ? getDate(val) : val)) as FilterValue)
          : isDate
          ? getDate(filterValue)
          : filterValue.toString()
        : filterValue.toString();
    }, [filterType, filterValue, isValueEqlParam]);

    const onChange = useCallback(
      (option: FilterTypeOption, newValue: FilterValue) => {
        changeComparisonOperator([option] as LayerFilterOption<ComparisonOperator>[]);
        setFilterValue(newValue instanceof Date ? formatDate(newValue, { dateFormat: DateFormat.UTC }) : newValue);
        changeValue(
          Array.isArray(newValue)
            ? newValue
                .map(val => (val instanceof Date ? `#'${formatDate(val, { dateFormat: DateFormat.UTC })}'` : val))
                .join(" - ")
            : newValue instanceof Date
            ? `#'${formatDate(newValue, { dateFormat: DateFormat.UTC })}'`
            : newValue,
        );
      },
      [changeComparisonOperator, changeValue],
    );

    useEffect(() => {
      const isNewOperator = prevOperator.current !== comparisonOperator;
      const isNewAttribute = prevAttribute.current !== attribute;

      if (isNewOperator || isNewAttribute) {
        setFilterValue(undefined);
        changeValue(undefined);
        setMin(undefined);
        setMax(undefined);

        if (isNewOperator) {
          prevOperator.current = comparisonOperator;
        }
        if (isNewAttribute) {
          prevAttribute.current = attribute;
        }
      }
    }, [comparisonOperator, attribute]); // eslint-disable-line

    useEffect(() => {
      if (isValueEqlParam) {
        if (!isParam) {
          toggleIsParam();
        }

        if (
          !Object.keys((layer.configuration as QueryLayerServiceConfigurationDc).eqlParameters).includes(
            condition.value as string,
          )
        ) {
          changeValue(undefined);
        }
      }
    }, []); // eslint-disable-line

    return (
      <ConditionBlank>
        <ConditionBlankInner>
          <ConditionAttributeContainer>
            <ConditionAttribute attribute={attribute} changeAttribute={changeAttribute} layerName={layer.name} />
          </ConditionAttributeContainer>
          <FilterInputContainer>
            {!isParam ? (
              <FilterInput
                label={t("value")}
                type={filterType}
                options={options}
                defaultOption={ComparisonOperator.Equals}
                selectedOption={selectedOption}
                zIndex={1001}
                value={getValue}
                asyncCallback={async newValue => {
                  const newOptions = await getValueOptions(newValue);

                  setOptions(newOptions);
                }}
                menuTitle={t("compare")}
                clearTitle={t("clearSingleFilter")}
                min={min}
                max={max}
                onShown={onShown}
                onChange={onChange}
              />
            ) : (
              <FilterInput
                label={t("param")}
                type="param"
                paramsList={Object.keys((layer.configuration as QueryLayerServiceConfigurationDc).eqlParameters)}
                options={getParamOperatorOptions()}
                defaultOption={ComparisonOperator.Equals}
                selectedOption={selectedOption}
                zIndex={1001}
                value={getValue}
                menuTitle={t("compare")}
                onChange={onChange}
              />
            )}
            {attribute !== null && isEqlWithParams && (
              <FilterInputParamSwitchContainer>
                <IconToggle
                  kind="param"
                  onClick={() => {
                    if (
                      !isParam &&
                      getParamOperatorOptions().findIndex(
                        item => item.value === (selectedOption as FilterTypeOption).value,
                      ) === -1
                    ) {
                      changeComparisonOperator([
                        getParamOperatorOptions()?.[0] as LayerFilterOption<ComparisonOperator>,
                      ]);
                    }

                    toggleIsParam();
                    setFilterValue(undefined);
                    changeValue(undefined);
                    setMin(undefined);
                    setMax(undefined);
                  }}
                  isSelected={isParam}
                />
              </FilterInputParamSwitchContainer>
            )}
          </FilterInputContainer>
          <ConditionDeleteButton kind={"delete"} title={t("removeQuery")} onClick={deleteCurrentCondition} />
        </ConditionBlankInner>
      </ConditionBlank>
    );
  },
);
